import React from 'react';
import styled, { keyframes } from 'styled-components';

const shimmer = keyframes`
  0% { background-position: -400px 0; }
  100% { background-position: 400px 0; }
`;

const SkeletonBase = styled.div`
  background: linear-gradient(90deg, #f2f1f3 25%, #e8e6eb 37%, #f2f1f3 63%);
  background-size: 800px 100%;
  animation: ${shimmer} 1.6s ease-in-out infinite;
  border-radius: ${(props) => props.$radius || '6px'};
`;

export const SkeletonLine = styled(SkeletonBase)`
  height: ${(props) => props.$height || '14px'};
  width: ${(props) => props.$width || '100%'};
  margin-bottom: ${(props) => props.$mb || '0'};
`;

export const SkeletonBlock = styled(SkeletonBase)`
  height: ${(props) => props.$height || '40px'};
  width: ${(props) => props.$width || '100%'};
`;

export const SkeletonCircle = styled(SkeletonBase)`
  width: ${(props) => props.$size || '32px'};
  height: ${(props) => props.$size || '32px'};
  border-radius: 50%;
  flex-shrink: 0;
`;

const CardSkeletonWrapper = styled.div`
  padding: 16px 20px;
  border-bottom: 1px solid #f2f1f3;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

export function CardSkeleton() {
  return (
    <CardSkeletonWrapper>
      <SkeletonLine $width="60%" $height="16px" />
      <SkeletonLine $width="90%" $height="12px" />
      <SkeletonLine $width="40%" $height="12px" />
    </CardSkeletonWrapper>
  );
}

const SidebarSkeletonWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
`;

const SidebarItemRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
`;

export function SidebarSkeleton({ items = 8 }) {
  return (
    <SidebarSkeletonWrapper>
      <SkeletonBlock $height="36px" $radius="8px" />
      {Array.from({ length: items }).map((_, i) => (
        <SidebarItemRow key={i}>
          <SkeletonCircle $size="25px" />
          <SkeletonLine $width={`${55 + (i % 3) * 15}%`} $height="14px" />
        </SidebarItemRow>
      ))}
    </SidebarSkeletonWrapper>
  );
}

const TaskListSkeletonWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: 24px;
  gap: 16px;
  width: 100%;
`;

const TaskCardSkeleton = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 20px;
  border-radius: 12px;
  border: 1px solid #f2f1f3;
  background: #fff;
`;

export function TaskListSkeleton({ items = 5 }) {
  return (
    <TaskListSkeletonWrapper>
      <SkeletonLine $width="200px" $height="24px" $mb="8px" />
      <SkeletonBlock $height="40px" $width="300px" $radius="20px" />
      <div style={{ display: 'flex', gap: '12px', marginTop: '8px' }}>
        {[120, 100, 80].map((w, i) => (
          <SkeletonBlock key={i} $height="32px" $width={`${w}px`} $radius="16px" />
        ))}
      </div>
      {Array.from({ length: items }).map((_, i) => (
        <TaskCardSkeleton key={i}>
          <SkeletonLine $width={`${50 + (i % 3) * 15}%`} $height="18px" />
          <SkeletonLine $width="80%" $height="13px" />
          <div style={{ display: 'flex', gap: '8px' }}>
            <SkeletonBlock $height="24px" $width="80px" $radius="12px" />
            <SkeletonBlock $height="24px" $width="60px" $radius="12px" />
          </div>
        </TaskCardSkeleton>
      ))}
    </TaskListSkeletonWrapper>
  );
}

const FeedbackSkeletonWrapper = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  height: calc(100vh - 150px);
`;

const FeedbackLeftPanel = styled.div`
  width: 30%;
  padding: 20px;
  border-right: 1px solid #f2f1f3;
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const FeedbackRightPanel = styled.div`
  width: 70%;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export function FeedbackPageSkeleton() {
  return (
    <FeedbackSkeletonWrapper>
      <FeedbackLeftPanel>
        <SkeletonLine $width="60%" $height="16px" $mb="8px" />
        <SkeletonBlock $height="36px" $radius="8px" />
        {Array.from({ length: 6 }).map((_, i) => (
          <SkeletonBlock key={i} $height="48px" $radius="6px" />
        ))}
      </FeedbackLeftPanel>
      <FeedbackRightPanel>
        <SkeletonLine $width="40%" $height="20px" $mb="12px" />
        <SkeletonBlock $height="200px" $radius="8px" />
        <SkeletonLine $width="30%" $height="16px" />
        <SkeletonBlock $height="120px" $radius="8px" />
      </FeedbackRightPanel>
    </FeedbackSkeletonWrapper>
  );
}

const CommentBankSkeletonWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: 20px;
  gap: 16px;
  width: 100%;
`;

export function CommentBankSkeleton({ items = 6 }) {
  return (
    <CommentBankSkeletonWrapper>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <SkeletonLine $width="180px" $height="22px" />
        <SkeletonBlock $height="36px" $width="140px" $radius="20px" />
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
        {Array.from({ length: items }).map((_, i) => (
          <SkeletonBlock
            key={i}
            $height="120px"
            $width="calc(33.33% - 8px)"
            $radius="12px"
          />
        ))}
      </div>
    </CommentBankSkeletonWrapper>
  );
}

const PageSkeletonWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  padding: 40px;
  gap: 20px;
`;

export function PageSkeleton() {
  return (
    <PageSkeletonWrapper>
      <SkeletonBlock $height="60px" $width="60px" $radius="50%" />
      <SkeletonLine $width="200px" $height="16px" />
    </PageSkeletonWrapper>
  );
}
